import React, { useEffect, useState } from "react";
import { Download, X } from "lucide-react";
import FloatingSlot from "@/components/FloatingSlot";

const DISMISS_KEY = "fn_install_dismissed_at";
const DISMISS_HOURS = 72;

export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    // Already installed as standalone?
    const standalone =
      window.matchMedia?.("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    if (standalone) return;

    const dismissed = Number(localStorage.getItem(DISMISS_KEY) || 0);
    if (dismissed && Date.now() - dismissed < DISMISS_HOURS * 3600 * 1000) return;

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    const onInstalled = () => { setInstalled(true); setVisible(false); };

    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);

    // iOS Safari: no beforeinstallprompt, show manual banner
    const ua = window.navigator.userAgent;
    const isIOS = /iphone|ipad|ipod/i.test(ua) && !window.MSStream;
    if (isIOS && !standalone) setVisible(true);

    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    const { outcome } = await deferred.userChoice;
    if (outcome === "accepted") setVisible(false);
    setDeferred(null);
  };

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false);
  };

  if (!visible || installed) return null;

  const isIOS = /iphone|ipad|ipod/i.test(window.navigator.userAgent) && !window.MSStream;

  return (
    <FloatingSlot
      zone="bottom-banner"
      order={1}
      size="fab"
      zIndex="banner"
      hideOnKeyboard={false}
      className="md:!inset-inline-start-auto md:max-w-96 md:ms-auto"
    >
      <div className="bg-white border border-stone-200 shadow-xl rounded-2xl p-4 flex items-start gap-3" data-testid="install-prompt">
        <div className="w-11 h-11 rounded-xl bg-[#E05E36] text-white grid place-items-center shrink-0 font-black">FN</div>
        <div className="flex-1 min-w-0">
          <div className="heading font-extrabold text-stone-900">Install Find2Near</div>
          {isIOS ? (
            <div className="text-xs text-stone-600 mt-0.5">
              Tap <span className="font-bold">Share</span> → <span className="font-bold">Add to Home Screen</span> to install.
            </div>
          ) : (
            <div className="text-xs text-stone-600 mt-0.5">One-tap install. Works offline. Fast like a native app.</div>
          )}
          {!isIOS && (
            <button
              onClick={install}
              className="mt-2 inline-flex items-center gap-2 bg-[#E05E36] hover:bg-[#c94e2a] text-white text-xs font-bold px-3 py-1.5 rounded-full"
              data-testid="install-btn"
            >
              <Download size={14} /> Install
            </button>
          )}
        </div>
        <button onClick={dismiss} className="text-stone-400 hover:text-stone-700" aria-label="Dismiss" data-testid="install-dismiss">
          <X size={18} />
        </button>
      </div>
    </FloatingSlot>
  );
}
